import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { HEADER_OTORISASI_EDIT, tokenUbahUntuk } from '@/lib/reEdit';
import { HEADER_OTORISASI_VOID, tokenVoidUntuk } from '@/lib/voidNota';

/**
 * Kunci sessionStorage alasan sesi diakhiri paksa (401: user dinonaktifkan, sesi habis, token tidak valid) — dibaca
 * sekali oleh halaman /login (gelombang 7: user nonaktif langsung terputus dengan pesan jelas).
 */
export const KUNCI_ALASAN_KELUAR = 'mgmx-alasan-keluar';

function getDynamicBaseUrl(): string {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;

        // Jika local development (localhost / 127.0.0.1)
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return `http://${hostname}:3045`;
        }

        // Jika di browser production / domain (Next.js rewrite handle /api proxy ke port 3045)
        return '';
    }

    // SSR (Server Side Rendering) -> langsung ke backend local 3045
    return 'http://localhost:3045';
}

const BASE_URL = getDynamicBaseUrl();

const api: AxiosInstance = axios.create({
    baseURL: BASE_URL ? `${BASE_URL}/api` : '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (typeof window !== 'undefined') {
            try {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const useAuthStore = require('@/stores/authStore').default;
                const state = useAuthStore.getState();
                const token: string | null = state.accessToken;

                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch {
                // Silently fail — store belum ready (SSR / first load)
            }
            // Re-Edit (lib/reEdit.ts): token otorisasi user lain untuk PUT ubah nota yang sudah diotorisasi
            const tokenUbah = tokenUbahUntuk(config.method, config.url);
            if (tokenUbah) config.headers[HEADER_OTORISASI_EDIT] = tokenUbah;
            // Void (lib/voidNota.ts): token otorisasi user lain untuk request void nota yang sudah diotorisasi
            const tokenVoid = tokenVoidUntuk(config.method, config.url);
            if (tokenVoid) config.headers[HEADER_OTORISASI_VOID] = tokenVoid;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        // 401 = sesi habis -> logout + kembali ke /login. PENGECUALIAN: request login itu sendiri
        // (password salah juga 401) dan request saat belum ada token - jangan logout/redirect, kalau tidak
        // halaman login dimuat ulang dan pesan "Login Gagal" hilang.
        if (error.response?.status === 401 && typeof window !== 'undefined') {
            try {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const useAuthStore = require('@/stores/authStore').default;
                const url = String(error.config?.url ?? '');
                const isLoginRequest = /(^|\/)auth\/login\/?(\?|$)/.test(url);
                const hasToken = !!useAuthStore.getState().accessToken;
                if (!isLoginRequest && hasToken) {
                    // Pesan BE (mis. 'User tidak aktif. Sesi diakhiri, ...') ditampilkan halaman login sesudah redirect
                    const pesan = error.response?.data?.message;
                    if (typeof pesan === 'string' && pesan.trim()) {
                        try { window.sessionStorage.setItem(KUNCI_ALASAN_KELUAR, pesan); } catch { /* storage diblokir */ }
                    }
                    useAuthStore.getState().logout();
                }
            } catch {
                // Silently fail
            }
        }
        return Promise.reject(error);
    }
);

export default api;
